import { useEffect, useMemo, useState } from "react";
import { useGetSchoolList } from "../../../../net/CharacterFeatureManagement";
import { TableSort } from "../../../../common/components/layout/Table/Table";
import { useWindowDimensions } from "../../../../common/utilities/WindowDimensions";
import Cookies from "universal-cookie";
import FlexRow from "../../../../common/components/layout/Flexbox/FlexRow";
import Modal from "../../../../common/components/modals/Modal/Modal";
import Table from "../../../../common/components/layout/Table/Table";
import TextField from "../../../../common/components/display/TextField/TextField";
import TextInput from "../../../../common/components/input/TextInput/TextInput";
import School from "../../../../utils/game/mechanics/School";
import SchoolSorter from "../../../../common/utilities/sorters/SchoolSorter";

import "../style/CommonModalStyle_style.css";

type Props = {
    isVisible?: boolean;
    schoolOptions?: Array<School>;
    /** Filters school options based on external properties */
    optionFilter?: ((s: School) => boolean) | null;
    onSchoolOptionsChanged?: (schools: Array<School>) => void;
    onClose?: () => void;
    onConfirm?: (schools: Array<School>) => void;
};

export default function LoadSchoolModal(props: Props): JSX.Element {
    const [loadSchoolList, isLoadingSchoolList] = useGetSchoolList();
    const [selectedSchoolIDs, setSelectedSchoolIDs] = useState<Array<number>>(
        []
    );
    const [schoolList, setSchoolList] = useState<Array<School>>(
        props.schoolOptions ?? []
    );
    const [schoolListSort, setSchoolListSort] =
        useState<TableSort | null>(null);
    const [searchStr, setSearchStr] = useState<string>("");

    const schoolSorter = useMemo(() => {
        return SchoolSorter.createSorter(schoolListSort?.key, schoolListSort?.order);}, [schoolListSort]);

    const cookies = new Cookies();

    const windowDimensions = useWindowDimensions();

    useEffect(() => {
        if (props.schoolOptions === undefined) {
            loadSchoolList({
                variables: { token: cookies.get("session_token") },
                onComplete: (result) => {
                    setSchoolList(result);
                    props.onSchoolOptionsChanged?.(result);
                },
                onError: (error) => {},
            });
        }
    }, []);

    const tableFilter = useMemo(() => {
        const searchFilter =
            searchStr.length > 0
                ? (school: School) => {
                      const match = new RegExp(searchStr, "i");
                      const result =
                          school.title.match(match) ||
                          school.description.match(match);

                      return (
                          (props.optionFilter == null ||
                              props.optionFilter(school)) &&
                          result !== null
                      );
                  }
                : null;

        const filters = [props.optionFilter, searchFilter].flatMap(
            (f) => f ?? []
        );
        return filters.length > 0
            ? (school: School) => filters.every((filter) => filter(school))
            : null;
    }, [props.optionFilter, searchStr]);

    return (
        <Modal
            contentClassName="character-common-modal"
            header="Add School"
            isVisible={props.isVisible}
            width={windowDimensions.width >= 900 ? 750 : 400}
            footer="standard-submit-cancel"
            onClose={props.onClose}
            onConfirm={() => {
                props.onConfirm?.(
                    schoolList.filter((school) =>
                        selectedSchoolIDs.includes(school.id)
                    )
                );
            }}
        >
            <FlexRow>
                <FlexRow>
                    <TextField
                        type="bold"
                        style={{ width: "auto", margin: "8px" }}
                    >
                        Search
                    </TextField>
                    <TextInput
                        value={searchStr}
                        style={{
                            width: `${
                                windowDimensions.width >= 900
                                    ? "240px"
                                    : "120px"
                            }`,
                        }}
                        onChange={setSearchStr}
                    />
                </FlexRow>
                <FlexRow alignment="end">
                    <TextField
                        type="bold"
                        style={{ width: "auto", margin: "8px" }}
                    >{`${selectedSchoolIDs.length} Selected`}</TextField>
                    <button
                        onClick={() => {
                            setSelectedSchoolIDs([]);
                        }}
                    >
                        Clear
                    </button>
                </FlexRow>
            </FlexRow>
            <hr />
            <Table
                className="options-list"
                columns={[
                    {
                        columnKey: "title",
                        columnHeader: "Title",
                        sortable: true,
                        width: 180,
                    },
                    {
                        columnKey: "description",
                        columnHeader: "Description",
                        sortable: true,
                    },
                ]}
                rowData={schoolList}
                cellParser={[
                    {
                        columnKey: "title",
                        callback: (school) => {
                            return (
                                <TextField className="option-item" numberOfLines={3}>
                                    {school.title}
                                </TextField>
                            );
                        },
                    },
                    {
                        columnKey: "description",
                        callback: (school) => {
                            return (
                                <TextField className="option-item" numberOfLines={15}>
                                    {school.description}
                                </TextField>
                            );
                        },
                    },
                ]}
                idGetter={(school) => school.id.toString()}
                sort={schoolListSort}
                onChangeSort={setSchoolListSort}
                sorter={schoolSorter}
                isRowSelected={(school) =>
                    selectedSchoolIDs.includes(school.id)
                }
                onRowClick={(school) => {
                    if (selectedSchoolIDs.includes(school.id)) {
                        setSelectedSchoolIDs([
                            ...selectedSchoolIDs.filter(
                                (entry) => entry !== school.id
                            ),
                        ]);
                    } else {
                        setSelectedSchoolIDs([...selectedSchoolIDs, school.id]);
                    }
                }}
                rowFilter={tableFilter}
                showHeader={true}
            />
        </Modal>
    );
}
